/**
 * WebSocket hook for the web terminal.
 * Manages connection lifecycle and message routing.
 */

import { useRef, useCallback, useEffect, useState } from 'react';

export type TerminalStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface UseTerminalOptions {
  onOutput: (data: string) => void;
  onExit?: (code: number) => void;
  onStatusChange?: (status: TerminalStatus) => void;
}

interface UseTerminalReturn {
  status: TerminalStatus;
  sessionId: string | null;
  connect: () => void;
  disconnect: () => void;
  sendInput: (data: string) => void;
  sendResize: (cols: number, rows: number) => void;
}

export function useTerminal({
  onOutput,
  onExit,
  onStatusChange,
}: UseTerminalOptions): UseTerminalReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const [status, setStatus] = useState<TerminalStatus>('disconnected');
  const [sessionId, setSessionId] = useState<string | null>(null);

  const updateStatus = useCallback(
    (s: TerminalStatus): void => {
      setStatus(s);
      onStatusChange?.(s);
    },
    [onStatusChange]
  );

  const connect = useCallback((): void => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    updateStatus('connecting');

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // In dev, Vite proxies are HTTP-only; use the API port directly for WebSocket
    const apiPort = import.meta.env.VITE_API_PORT;
    const host = apiPort ? `${window.location.hostname}:${apiPort}` : window.location.host;
    const ws = new WebSocket(`${protocol}//${host}/ws/terminal`);

    ws.onopen = (): void => {
      updateStatus('connected');
      // Create a shell session
      ws.send(JSON.stringify({ type: 'create' }));
    };

    ws.onmessage = (event: MessageEvent): void => {
      const msg = JSON.parse(event.data as string) as {
        type: string;
        data?: string;
        code?: number;
        sessionId?: string;
        message?: string;
      };

      switch (msg.type) {
        case 'output':
          if (msg.data) onOutput(msg.data);
          break;
        case 'ready':
          if (msg.sessionId) setSessionId(msg.sessionId);
          break;
        case 'exit':
          onExit?.(msg.code ?? 0);
          break;
        case 'error':
          console.error('Terminal error:', msg.message);
          break;
      }
    };

    ws.onerror = (): void => {
      updateStatus('error');
    };

    ws.onclose = (): void => {
      updateStatus('disconnected');
      setSessionId(null);
      wsRef.current = null;
    };

    wsRef.current = ws;
  }, [onOutput, onExit, updateStatus]);

  const disconnect = useCallback((): void => {
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'close' }));
      wsRef.current.close();
      wsRef.current = null;
    }
    updateStatus('disconnected');
    setSessionId(null);
  }, [updateStatus]);

  const sendInput = useCallback((data: string): void => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'input', data }));
    }
  }, []);

  const sendResize = useCallback((cols: number, rows: number): void => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
    }
  }, []);

  // Cleanup on unmount
  useEffect((): (() => void) => {
    return (): void => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  return {
    status,
    sessionId,
    connect,
    disconnect,
    sendInput,
    sendResize,
  };
}
